export interface StockQuote {
  price: number | null;
  change: number | null;
  changePercent: number | null;
  currency: string | null;
  name: string | null;
}

export interface StockChartPoint {
  timestamp: number;
  price: number;
}

interface StockPriceResponse {
  quote: StockQuote;
  chart: StockChartPoint[];
}

// In-memory cache
const cache = new Map<string, { data: StockPriceResponse; ts: number }>();
const CACHE_TTL = 5 * 60_000; // 5 min

export type StockRange = '1d' | '5d' | '1mo' | '3mo' | '6mo' | '1y';

export async function getStockData(
  symbol: string,
  range: StockRange = '1mo'
): Promise<StockPriceResponse | null> {
  const key = `${symbol}_${range}`;
  const cached = cache.get(key);
  if (cached && Date.now() - cached.ts < CACHE_TTL) return cached.data;

  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
  if (!supabaseUrl || !anonKey) return null;

  try {
    const url = `${supabaseUrl}/functions/v1/stock-price?symbol=${encodeURIComponent(symbol)}&range=${range}`;
    const res = await fetch(url, {
      headers: {
        Authorization: `Bearer ${anonKey}`,
        apikey: anonKey,
      },
    });

    if (!res.ok) return null;
    const result: StockPriceResponse = await res.json();
    cache.set(key, { data: result, ts: Date.now() });
    return result;
  } catch {
    return null;
  }
}
